import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Loader2,
  PackagePlus,
  Search,
  Sparkles,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, apiJson, ApiError, orgPath } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { EcosystemMark } from '@/components/hawk-primitives'

export interface RequestPackageRepo {
  name: string
  ecosystem: string
  upstream_base: string | null
}

interface UpstreamVersion {
  version: string
  yanked: boolean
  yanked_reason: string | null
  released_at: string | null
}

interface UpstreamVersionList {
  package_name: string
  versions: UpstreamVersion[]
  url: string
  yank_supported: boolean
}

interface CreateResponse {
  id: string
  status: string
  check_status: number | null
  check_result: Record<string, unknown> | null
  package_name: string
  package_version: string | null
  repository_name: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  repo?: RequestPackageRepo
  availableRepos?: RequestPackageRepo[]
}

type Step = 'pick-package' | 'submitted'

export function RequestPackagePanel({
  open,
  onOpenChange,
  repo,
  availableRepos,
}: Props) {
  const queryClient = useQueryClient()
  const [selectedRepoName, setSelectedRepoName] = useState<string>(
    repo?.name ?? '',
  )
  const [packageName, setPackageName] = useState('')
  const [debouncedName, setDebouncedName] = useState('')
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null) // null = any/latest
  const [versionFilter, setVersionFilter] = useState('')
  const [note, setNote] = useState('')
  const [step, setStep] = useState<Step>('pick-package')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CreateResponse | null>(null)

  useEffect(() => {
    if (open) {
      setSelectedRepoName(repo?.name ?? availableRepos?.[0]?.name ?? '')
      setPackageName('')
      setDebouncedName('')
      setSelectedVersion(null)
      setVersionFilter('')
      setNote('')
      setStep('pick-package')
      setError(null)
      setResult(null)
    }
  }, [open, repo, availableRepos])

  // Debounce package name for the lookup
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(packageName.trim()), 350)
    return () => clearTimeout(t)
  }, [packageName])

  const selectedRepo =
    repo ?? availableRepos?.find((r) => r.name === selectedRepoName)

  const versionsQuery = useOrgQuery<UpstreamVersionList>(
    ['upstream-versions', selectedRepoName, debouncedName],
    `/repositories/${encodeURIComponent(selectedRepoName)}/upstream-versions?package=${encodeURIComponent(debouncedName)}`,
    {
      enabled: !!selectedRepoName && debouncedName.length >= 1,
      retry: false,
      staleTime: 60_000,
    },
  )

  // Reset picked version + filter when the package changes
  useEffect(() => {
    setSelectedVersion(null)
    setVersionFilter('')
  }, [debouncedName, selectedRepoName])

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(orgPath('/requests'), {
        method: 'POST',
        body: JSON.stringify({
          repository_name: selectedRepoName,
          package_name: packageName.trim(),
          package_version: selectedVersion,
          note: note.trim() || null,
        }),
      })
      if (!response.ok) {
        const body = await response.text()
        throw new Error(body || `HTTP ${response.status}`)
      }
      return (await response.json()) as CreateResponse
    },
    onSuccess: (data) => {
      setResult(data)
      setStep('submitted')
      toast.success(`Request submitted for ${data.package_name}`)
      queryClient.invalidateQueries({ queryKey: ['admin-requests'] })
      queryClient.invalidateQueries({ queryKey: ['admin-requests-pending'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  function submit() {
    setError(null)
    if (!selectedRepoName) return setError('Choose a repository')
    if (!packageName.trim()) return setError('Package name is required')
    mutation.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex flex-col gap-0"
      >
        <PanelHeader onClose={() => onOpenChange(false)} />

        {step === 'submitted' && result ? (
          <ResultView result={result} onClose={() => onOpenChange(false)} />
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Repo: locked tile or picker */}
              {repo ? (
                <RepoTile repo={repo} />
              ) : (
                availableRepos && (
                  <Field label="Repository">
                    <Select
                      value={selectedRepoName}
                      onValueChange={setSelectedRepoName}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a repository..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRepos.map((r) => (
                          <SelectItem key={r.name} value={r.name}>
                            {r.name} ({r.ecosystem})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )
              )}

              {/* Package name with live search */}
              <Field label="Package">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/60 pointer-events-none" />
                  <Input
                    value={packageName}
                    onChange={(e) => setPackageName(e.target.value)}
                    placeholder={
                      selectedRepo?.ecosystem === 'npm'
                        ? 'lodash or @scope/name'
                        : selectedRepo?.ecosystem === 'pypi'
                          ? 'requests'
                          : selectedRepo?.ecosystem === 'cargo'
                            ? 'serde'
                            : 'package-name'
                    }
                    autoFocus
                    maxLength={200}
                    className="pl-9 font-mono text-sm"
                  />
                  {versionsQuery.isFetching && debouncedName && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/60 animate-spin" />
                  )}
                </div>
              </Field>

              {/* Versions */}
              <VersionsSection
                query={versionsQuery}
                searchTerm={debouncedName}
                selectedVersion={selectedVersion}
                onSelectVersion={setSelectedVersion}
                filter={versionFilter}
                onFilterChange={setVersionFilter}
              />

              {/* Optional reason */}
              <Field label="Reason" hint="optional">
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why this package is needed"
                  maxLength={1000}
                />
              </Field>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
                  <AlertCircle
                    className="size-4 text-destructive shrink-0 mt-0.5"
                    strokeWidth={1.5}
                  />
                  <p className="text-xs text-destructive flex-1 leading-relaxed">
                    {error}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-border bg-background/40 px-6 py-4 flex items-center justify-between gap-2">
              <SummaryLine
                packageName={packageName}
                version={selectedVersion}
              />
              <div className="flex items-center gap-2">
                <SheetClose asChild>
                  <Button type="button" variant="ghost">
                    Cancel
                  </Button>
                </SheetClose>
                <Button
                  onClick={submit}
                  disabled={mutation.isPending || !packageName.trim()}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <PackagePlus className="size-4" strokeWidth={1.5} />
                      Submit request
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export { RequestPackagePanel as RequestPackageDialog }

function PanelHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="relative px-6 pt-6 pb-5 border-b border-border">
      <div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none"
        aria-hidden
      />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="size-9 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center text-primary shrink-0">
            <PackagePlus className="size-4" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <SheetTitle className="text-base tracking-tight">
              Request a package
            </SheetTitle>
            <SheetDescription className="text-xs mt-0.5">
              Type a name to browse all available versions.
            </SheetDescription>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          className="text-muted-foreground -mr-2 -mt-1"
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </Button>
      </div>
    </div>
  )
}

function RepoTile({ repo }: { repo: RequestPackageRepo }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-secondary/60 border border-border">
      <EcosystemMark ecosystem={repo.ecosystem} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{repo.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {repo.ecosystem}
        </div>
      </div>
    </div>
  )
}

function VersionsSection({
  query,
  searchTerm,
  selectedVersion,
  onSelectVersion,
  filter,
  onFilterChange,
}: {
  query: ReturnType<typeof useOrgQuery<UpstreamVersionList>>
  searchTerm: string
  selectedVersion: string | null
  onSelectVersion: (v: string | null) => void
  filter: string
  onFilterChange: (s: string) => void
}) {
  const versions = query.data?.versions ?? []
  const filterLower = filter.trim().toLowerCase()
  const filtered = useMemo(
    () =>
      filterLower
        ? versions.filter((v) => v.version.toLowerCase().includes(filterLower))
        : versions,
    [versions, filterLower],
  )
  const yankedCount = versions.filter((v) => v.yanked).length

  // States
  if (searchTerm.length < 1) {
    return (
      <div className="border border-border border-dashed rounded-md p-6 text-center bg-card/40">
        <Sparkles
          className="size-5 text-muted-foreground/50 mx-auto mb-2"
          strokeWidth={1.5}
        />
        <p className="text-sm text-muted-foreground">
          Type a package name to see its versions.
        </p>
      </div>
    )
  }

  if (query.isLoading || query.isFetching) {
    return (
      <div className="border border-border rounded-md p-6 text-center bg-card/40">
        <Loader2 className="size-4 animate-spin text-muted-foreground/60 mx-auto mb-2" />
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Looking up upstream
        </p>
      </div>
    )
  }

  if (query.isError) {
    const err = query.error as ApiError | Error
    const is404 =
      err instanceof Error &&
      'status' in err &&
      (err as ApiError).status === 404
    return (
      <div className="border border-destructive/30 rounded-md p-4 bg-destructive/5">
        <div className="flex items-start gap-2">
          <AlertCircle
            className="size-4 text-destructive shrink-0 mt-0.5"
            strokeWidth={1.5}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-destructive">
              {is404
                ? `No package named "${searchTerm}" upstream.`
                : 'Could not load versions from upstream.'}
            </p>
            {!is404 && (
              <p className="text-xs text-muted-foreground mt-1">
                You can still submit the request, an admin will see it.
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (versions.length === 0) {
    return (
      <div className="border border-border rounded-md p-6 text-center bg-card/40">
        <p className="text-sm text-muted-foreground">
          No versions found for this package.
        </p>
      </div>
    )
  }

  const customMatch = filterLower
    ? versions.find((v) => v.version.toLowerCase() === filterLower)
    : undefined
  const showCustomEscape =
    filterLower.length > 0 && filtered.length === 0 && !customMatch

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Versions
          <span className="ml-2 text-foreground/60 tabular-nums">
            {versions.length}
          </span>
          {yankedCount > 0 && (
            <span className="ml-2 text-destructive/80 tabular-nums">
              ({yankedCount} yanked)
            </span>
          )}
        </span>
        {filterLower && (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums">
            {filtered.length} match
          </span>
        )}
      </div>

      {/* Filter / autocomplete */}
      {versions.length > 5 && (
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/60 pointer-events-none" />
          <Input
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Filter versions..."
            className="pl-9 pr-8 h-8 font-mono text-xs bg-secondary/60 border-transparent focus:border-primary/30"
          />
          {filter && (
            <button
              type="button"
              onClick={() => onFilterChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground"
              aria-label="Clear filter"
            >
              <X className="size-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
      )}

      <div className="border border-border rounded-md bg-card overflow-hidden">
        {!filterLower && (
          <VersionRow
            label="Latest / any version"
            sublabel="Let the registry resolve at install time"
            selected={selectedVersion === null}
            onSelect={() => onSelectVersion(null)}
            icon={<ArrowRight className="size-3" strokeWidth={1.5} />}
          />
        )}

        <div className="max-h-[300px] overflow-y-auto">
          {filtered.map((v) => (
            <VersionRow
              key={v.version}
              label={v.version}
              mono
              yanked={v.yanked}
              selected={selectedVersion === v.version}
              onSelect={() => onSelectVersion(v.version)}
              meta={
                v.released_at && (
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground tabular-nums shrink-0">
                    <Clock className="size-2.5" strokeWidth={1.5} />
                    {formatDate(v.released_at)}
                  </span>
                )
              }
              badge={v.yanked && <YankedBadge reason={v.yanked_reason} />}
            />
          ))}
          {showCustomEscape && (
            <VersionRow
              label={`Use "${filter.trim()}" anyway`}
              sublabel="Submit this version even though upstream doesn't list it"
              custom
              selected={selectedVersion === filter.trim()}
              onSelect={() => onSelectVersion(filter.trim())}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function VersionRow({
  label,
  sublabel,
  mono,
  yanked,
  custom,
  selected,
  onSelect,
  icon,
  meta,
  badge,
}: {
  label: string
  sublabel?: string
  mono?: boolean
  yanked?: boolean
  custom?: boolean
  selected: boolean
  onSelect: () => void
  icon?: React.ReactNode
  meta?: React.ReactNode
  badge?: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-t border-border first:border-t-0 ${
        selected
          ? 'bg-primary/10 hover:bg-primary/15'
          : custom
            ? 'bg-background/40 hover:bg-popover'
            : 'hover:bg-popover'
      }`}
    >
      {selected && (
        <span
          className="absolute left-0 top-0 bottom-0 w-[2px] bg-primary"
          aria-hidden
        />
      )}
      <span
        className={`size-4 rounded-full shrink-0 flex items-center justify-center ${
          selected
            ? 'border border-primary bg-primary/20'
            : 'border border-border'
        }`}
      >
        {selected && <span className="size-1.5 rounded-full bg-primary" />}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`${
            mono ? 'font-mono text-[13px]' : 'text-sm'
          } block truncate ${
            yanked
              ? 'text-muted-foreground line-through decoration-destructive/60'
              : selected
                ? 'text-foreground'
                : 'text-foreground/90'
          }`}
        >
          {label}
        </span>
        {sublabel && (
          <span className="block text-[11px] text-muted-foreground truncate">
            {sublabel}
          </span>
        )}
      </span>
      {badge}
      {meta}
      {icon && <span className="text-muted-foreground/60">{icon}</span>}
    </button>
  )
}

function YankedBadge({ reason }: { reason: string | null }) {
  return (
    <span
      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/30"
      title={reason ?? 'Yanked'}
    >
      Yanked
    </span>
  )
}

function SummaryLine({
  packageName,
  version,
}: {
  packageName: string
  version: string | null
}) {
  if (!packageName.trim()) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        Enter a package
      </span>
    )
  }
  return (
    <span className="font-mono text-xs text-muted-foreground truncate">
      Will request{' '}
      <span className="text-foreground">{packageName.trim()}</span>
      {version ? (
        <>
          <span className="text-primary mx-0.5">@</span>
          <span className="text-primary">{version}</span>
        </>
      ) : (
        <span className="text-muted-foreground/70"> (any version)</span>
      )}
    </span>
  )
}

function ResultView({
  result,
  onClose,
}: {
  result: CreateResponse
  onClose: () => void
}) {
  const verified = result.status === 'verified'

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
        <div
          className={`flex items-start gap-3 p-4 rounded-md ring-1 ring-inset ${
            verified
              ? 'bg-primary/10 ring-primary/30'
              : 'bg-secondary/60 ring-border'
          }`}
        >
          {verified ? (
            <CheckCircle2
              className="size-5 text-primary shrink-0 mt-0.5"
              strokeWidth={1.5}
            />
          ) : (
            <AlertCircle
              className="size-5 text-muted-foreground shrink-0 mt-0.5"
              strokeWidth={1.5}
            />
          )}
          <div className="text-sm leading-relaxed">
            {verified
              ? 'Verified upstream. An admin will review your request next.'
              : 'Could not verify upstream right now. An admin will still review it.'}
          </div>
        </div>

        <dl className="space-y-3">
          <DetailRow label="Package" value={result.package_name} mono />
          {result.package_version && (
            <DetailRow label="Version" value={result.package_version} mono />
          )}
          <DetailRow label="Repository" value={result.repository_name} mono />
          <DetailRow
            label="Status"
            value={
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                pending review
              </span>
            }
          />
        </dl>
      </div>

      <div className="border-t border-border bg-background/40 px-6 py-4 flex items-center justify-end gap-2">
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  )
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 items-center text-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
      <div
        className={
          mono ? 'font-mono text-xs text-foreground/90 truncate' : 'text-foreground'
        }
      >
        {value}
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-2">
      <label className="flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <span>{label}</span>
        {hint && (
          <span className="text-muted-foreground/50 normal-case tracking-normal">
            {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  )
}

